// React
import { useState, useCallback } from 'react'

// MRL
import { observer } from 'mobx-react'

// Components
import { BaseImage, BaseLabel, FormLabel, SharedPhotoProfile } from 'components'

// Antd
import { Button, Menu, Dropdown, Spin } from 'antd'
import {
  MenuOutlined,
  UserOutlined,
  FileSearchOutlined,
  LogoutOutlined,
  CaretDownOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons'

// i18n
import { useTranslation } from 'react-i18next'

// React Router Dom
import { Link, useHistory } from 'react-router-dom'

// Lookup
import { navigations } from './lookup'

// Interfaces
import { IBaseHeaderNavigations } from './interfaces'

// Lodash
import isEmpty from 'lodash/isEmpty'

// Constant
import { AUTH_URL, PERSONAL_DATA_URL } from 'constant/url.constant'

// Custom Hook
import { useAuth } from 'hooks'

// Utils
import { oidc_userManager } from 'utils'
import { messageUtils_error, messageUtils_warning } from 'utils/common/message.utils'

// Constant
import {
  LOCAL_STORAGE_ACCESS_TOKEN,
  LOCAL_STORAGE_AUTHENTICATED_USER,
  LOCAL_STORAGE_IS_AUTHENTICATED,
} from 'constant/local-storage.constant'

// Styles
import './styles.scss'

// Menu
const { SubMenu } = Menu

const BaseHeader = observer(() => {
  // Hook
  const { t } = useTranslation()
  const { auth_accessToken, auth_authenticatedUser, auth_authenticatedUserRoles, auth_persisted } = useAuth()
  const [mobileMenuStatus, setMobileMenuStatus] = useState<boolean>(false)
  const [isLogoutLoading, setIsLogoutLoading] = useState<boolean>(false)
  const history = useHistory()
  // eslint-disable-next-line
  const [isAuthenticatedDropdownVisible, setIsAuthenticatedDropdownVisible] = useState<boolean>(false)

  const listUserMenu =
    auth_persisted?.auth_authenticatedUser?.roles?.length !== 0
      ? [
          { title: 'app.profile', route: PERSONAL_DATA_URL.PROFILE, icon: 'profile' },
          auth_persisted?.auth_authenticatedUser?.roles?.length! > 1 && {
            title: 'app.changeRole',
            route: AUTH_URL.CHANGE_ROLE,
            icon: 'changeRole',
          },
        ]
      : [{ title: 'app.profile', route: PERSONAL_DATA_URL.PROFILE, icon: 'profile' }]

  /**
   * @description Logout
   *
   * @return {Promise<void>} Promise<void>
   */
  const onLogout = useCallback(async (): Promise<void> => {
    setIsLogoutLoading(true)

    try {
      localStorage.removeItem(LOCAL_STORAGE_ACCESS_TOKEN)
      localStorage.removeItem(LOCAL_STORAGE_AUTHENTICATED_USER)
      localStorage.removeItem(LOCAL_STORAGE_IS_AUTHENTICATED)

      await oidc_userManager().signoutRedirect()

      messageUtils_warning(t('auth.duende.willBeRedirectedToSignOut'))
    } catch (err) {
      if (err instanceof Error) messageUtils_error(err.message)
    } finally {
      setIsLogoutLoading(false)
    }
  }, [t])

  /**
   * @description Get icon
   *
   * @param {string} iconType
   *
   * @return {JSX.Element} JSX.Element
   */
  const getIcon = (iconType: string): JSX.Element => {
    switch (iconType) {
      case 'profile':
        return <UserOutlined className="mr-2" />
      case 'submission-certification-status':
        return <FileSearchOutlined className="mr-2" />
      case 'logout':
        return <LogoutOutlined className="mr-2" />
      case 'changeRole':
        return <UserSwitchOutlined className="mr-2" />
      default:
        return <></>
    }
  }

  /**
   * @description Render navigation active
   *
   * @param {IBaseHeaderNavigations} navigation
   *
   * @return {boolean} boolean
   */
  const renderActiveNavigation = (navigation: IBaseHeaderNavigations): boolean => {
    return history.location.pathname === '/' && navigation.id === 'HOME'
      ? true
      : history.location.pathname === navigation.route && navigation.id !== 'HOME'
  }

  /**
   * @description User menu component
   *
   * @return {JSX.Element}
   */
  const UserMenuComponent = (): JSX.Element => {
    return (
      <Menu defaultSelectedKeys={[window.location.pathname]}>
        {/* Dropdown For Profile Info */}
        <Menu.Item key="0" style={{ cursor: 'default', backgroundColor: 'white' }} className={'header-photo-profile'}>
          <div className="flex items-center mb-4">
            <SharedPhotoProfile authenticatedUser={auth_authenticatedUser} isExpanded />
            <div className="flex flex-col items-center justify-center">
              {auth_authenticatedUserRoles.length !== 0 && (
                <FormLabel label={auth_authenticatedUserRoles.find(role => role.status)?.name || ''} className="font-11 ml-3" />
              )}
              <span className="font-16 ml-3">{auth_authenticatedUser?.name}</span>
            </div>
          </div>
          <hr />
        </Menu.Item>

        {/* Menus */}
        {listUserMenu
          .filter(menu => menu)
          .map(menu => {
            if (menu) {
              return (
                <Menu.Item key={menu.route}>
                  <Link to={menu.route} className="base-header-nav font-weight flex items-center">
                    {getIcon(menu.icon)}
                    <span>{t(menu.title)}</span>
                  </Link>
                </Menu.Item>
              )
            } else {
              return <></>
            }
          })}

        {/* Logout */}
        <Menu.Item key="4">
          <div className="flex items-center base-header-nav" onClick={onLogout}>
            <Spin spinning={isLogoutLoading}>
              <LogoutOutlined className="mr-2" />
              <span>Logout</span>
            </Spin>
          </div>
        </Menu.Item>
      </Menu>
    )
  }

  return (
    <div className="base-header">
      <div className="base-header-container flex items-center justify-between">
        {/* Logo */}
        <div className="base-header-logo">
          <BaseImage isImage image="lsp-logo" format="svg" onClick={() => history.push('/')} />
        </div>

        {/* Desktop menu section start */}
        <div className="menu">
          {(auth_persisted?.auth_authenticatedUser?.isValidated || !auth_persisted?.auth_accessToken) &&
            navigations.map((navigation: IBaseHeaderNavigations) => (
              <Link key={navigation.route} to={navigation.route} className="base-header-nav font-weight">
                <BaseLabel label={t(navigation.title)} fontSize={16} isBold={renderActiveNavigation(navigation)} />
              </Link>
            ))}

          {/* Buttons */}
          {isEmpty(auth_accessToken) ? (
            <div className="flex items-center justify-between">
              <Button
                type="primary"
                style={{ width: '120px', fontWeight: 700, marginLeft: 20, height: 30 }}
                ghost
                onClick={() => history.push(AUTH_URL.LOGIN)}
              >
                {t('app.login')}
              </Button>
            </div>
          ) : (
            <div className="flex items-center ml-5">
              <Dropdown
                placement="bottomRight"
                // visible={isAuthenticatedDropdownVisible}
                overlay={UserMenuComponent}
                trigger={['click']}
              >
                <div
                  className="flex items-center cursor-pointer"
                  // onClick={() => setIsAuthenticatedDropdownVisible(!isAuthenticatedDropdownVisible)}
                >
                  <SharedPhotoProfile authenticatedUser={auth_authenticatedUser} />
                  <div className="flex flex-col items-center">
                    <span className="font-16 ml-2">{auth_authenticatedUser?.name?.split(' ')?.[0] || ''}</span>
                  </div>
                  <CaretDownOutlined className="ml-2" />
                </div>
              </Dropdown>
            </div>
          )}
        </div>
        {/* Desktop menu section end */}

        {/* Mobile menu section start */}
        <MenuOutlined className="mobile-nav" onClick={() => setMobileMenuStatus(!mobileMenuStatus)} />
        <div className={`mobile-menu ${mobileMenuStatus ? 'active' : ''}`}>
          {navigations.map((navigation: IBaseHeaderNavigations) => (
            <Link key={navigation.route} to={navigation.route} className="base-header-nav font-weight">
              <BaseLabel label={t(navigation.title)} fontSize={16} isBold={history.location.pathname === navigation.route} />
            </Link>
          ))}

          {/* Menu user start */}
          {isEmpty(auth_accessToken) ? (
            <div className="flex items-center px-10 py-5">
              <Button type="primary" style={{ width: '100%' }} ghost onClick={() => history.push('/auth/login')}>
                {t('app.login')}
              </Button>
            </div>
          ) : (
            <div>
              <Menu
                style={{ width: '100%' }}
                // defaultSelectedKeys={['1']}
                defaultOpenKeys={['']}
                mode="inline"
              >
                <SubMenu
                  key="userMenu"
                  title={`${auth_authenticatedUser.name} - ${auth_authenticatedUserRoles.find(role => role.status)?.name || ''}`}
                >
                  <UserMenuComponent />
                </SubMenu>
              </Menu>
            </div>
          )}
          {/* menu user end */}
        </div>
        {/* mobile menu section end */}
      </div>
    </div>
  )
})

export { BaseHeader }
